/**
 * Bulk upload: the blank workbook on the way out, the filled one on the way
 * back in, and the refused rows on the way back out again.
 *
 * That last one is the shape of the whole feature. A sheet of 160 dishes is
 * not a thing that succeeds or fails — 130 rows land and 30 are refused, and
 * what the dietitian needs is not an error message but the 30 rows back, in a
 * workbook, with a remark against each. So `importDishes` resolves for a
 * partly-refused sheet rather than throwing: only the request failing is a
 * failure. What came back is a report, and the report is a screen.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type {
  DishImportErrorResponse,
  DishImportProblem,
  DishImportResponse,
  DishImportResult,
} from "../types/dishes.types";

/** What the picker takes, and what the dialog says it takes. */
export const ACCEPTED_EXTENSIONS = [".xls", ".xlsx"] as const;

/** The `accept` attribute, kept next to the list the check below reads. */
export const ACCEPTED_ATTR = ACCEPTED_EXTENSIONS.join(",");

/** 5MB, which is the figure printed under the drop zone. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** What the downloaded template is saved as. */
const TEMPLATE_FILE_NAME = "naurist-bulk-dish-upload.xlsx";

/** What the error workbook is saved as, where the server did not name it. */
const ERROR_SHEET_FALLBACK_NAME = "dish-import-errors.xlsx";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * What the template download asks for.
 *
 * `XLSX_MIME` alone comes back 406, before the request is even authenticated:
 * DRF negotiates the response against the renderers registered on the view,
 * and the spreadsheet type is not one of them — so asking for precisely the
 * thing the endpoint exists to hand over is the one way to be refused it.
 *
 * Hence the wildcard on the end, at the lowest weight. The specific types stay
 * in front and keep their precedence, so a view that does register a
 * spreadsheet renderer later still serves that; the wildcard is only what gets
 * today's view past negotiation. If it answers with JSON anyway, `api.getBlob`
 * catches that on the content type and raises the server's own message rather
 * than saving a file that opens as nothing.
 */
const TEMPLATE_ACCEPT = `${XLSX_MIME}, application/vnd.ms-excel;q=0.9, */*;q=0.8`;

async function requireToken(getToken: GetToken, action: string): Promise<string> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${action}.`);
  }
  return token;
}

/**
 * Why a file cannot be sent, or nothing if it can.
 *
 * Checked on the extension rather than on `file.type`: Windows reports an
 * `.xlsx` picked from some applications with an empty MIME type and an `.xls`
 * with any of three, so the name is the more reliable of the two here — unlike
 * an image, where `UploadField` can trust the browser's sniffing.
 */
export const uploadProblem = (file: File): string | null => {
  const name = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    return "Only .xls and .xlsx files can be uploaded";
  }
  if (file.size > MAX_UPLOAD_BYTES) return "File is larger than 5MB";
  // An empty sheet is a file the server would reject after a round trip, and
  // the reader is standing at the dialog either way.
  if (file.size === 0) return "That file is empty";
  return null;
};

/** `1.4 MB` — what the chosen file's row shows beside its name. */
export const fileSizeLabel = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

/**
 * Hands a file to the browser.
 *
 * The object URL is released after the click — the download has been handed
 * off by then, and holding it would keep the file in memory for the life of
 * the tab. The same shape `savePlanPdf` takes, for the same reason.
 */
const saveFile = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/** The blank workbook, `GET /api/v1/workspace-foods/import/template/`. */
export async function downloadDishTemplate(getToken: GetToken): Promise<void> {
  const token = await requireToken(getToken, "download the dish template");

  const workbook = await api.getBlob(API_ENDPOINTS.workspaceFoods.importTemplate, {
    token,
    headers: { Accept: TEMPLATE_ACCEPT },
  });

  saveFile(workbook, TEMPLATE_FILE_NAME);
}

/**
 * A refused row, as the report grid reads it.
 *
 * The name is pulled out of the row's own cells rather than from a field of
 * its own, because that is where it is: the object carries the line as it was
 * typed, and the name column is one of those cells. It is taken as a string
 * whatever it holds — the example response has `"147.68"` under `name`, which
 * is a reader who filled the columns out of order, and the grid should show
 * them what they actually typed rather than a blank.
 */
const toProblem = (row: DishImportErrorResponse): DishImportProblem => ({
  row: row.row,
  name: typeof row.name === "string" ? row.name : row.name == null ? "" : String(row.name),
  reason: row.error_reason ?? "",
  columns: row.columns ?? [],
  errorType: row.error_type ?? "",
});

/**
 * Sends the filled sheet and reads the tally back.
 *
 * Rejects only when the request does — a network failure, a 4xx, a sheet the
 * server could not open at all. A sheet it could open is a resolved promise
 * however many of its rows were refused, and `failed` is what says so.
 */
export async function importDishes(
  getToken: GetToken,
  file: File
): Promise<DishImportResult> {
  const token = await requireToken(getToken, "upload dishes");

  const body = new FormData();
  body.append("file", file);

  // The Content-Type is the browser's to set here, boundary and all — see
  // `api.post`, which leaves FormData alone for exactly that reason.
  const response = await api.post<DishImportResponse>(
    API_ENDPOINTS.workspaceFoods.import,
    body,
    { token }
  );

  const imported = response?.imported ?? 0;
  const updated = response?.updated ?? 0;
  const failed = response?.failed ?? 0;
  const sheet = response?.error_sheet;

  return {
    imported,
    updated,
    failed,
    succeeded: imported + updated,
    totalRows: imported + updated + failed,
    problems: (response?.errors ?? []).map(toProblem),
    // Only where there is something in it. An empty string is the same as no
    // workbook, and would otherwise put a Download button on the report that
    // saves a nought-byte file.
    errorSheet: sheet
      ? {
          base64: sheet,
          fileName: response?.error_sheet_filename || ERROR_SHEET_FALLBACK_NAME,
        }
      : null,
  };
}

/**
 * Saves the refused rows as a workbook.
 *
 * The bytes came over in the JSON, so there is no call to make here — the
 * decode is the whole of it. `atob` throws on anything that is not base64,
 * which is caught into a message rather than left to reach the console as a
 * DOMException nobody can act on.
 */
export function saveDishImportErrors(sheet: {
  base64: string;
  fileName: string;
}): void {
  let binary: string;
  try {
    binary = atob(sheet.base64);
  } catch {
    throw new Error("That file could not be read. Try uploading the sheet again.");
  }

  // A byte at a time, because `atob` gives back a string of char codes rather
  // than bytes, and a Blob built from that string would be UTF-8 encoded on
  // the way in — which turns every code above 127 into two bytes and the
  // workbook into something Excel will not open.
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  saveFile(new Blob([bytes], { type: XLSX_MIME }), sheet.fileName);
}
